import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// components
import { Iconify, Label } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import AddressWithGoogleMapsIconButton from '@/components/AddressWithGoogleMapsIconButton';
import {
  VBMAccordion,
  VBMApplyCouponDialog,
  VBMCloneSessionDialog,
} from '../bookings/view';
// hooks + utils
import useToggle from '@/hooks/useToggle';
import useAuth from '@/hooks/useAuth';
import { bookingsManagementPath } from '@/features/bookings-management';
import { clientManagementPath } from '@/features/users/clients';
import { providersPath } from '@/features/users/providers';
import {
  getArrivalRiskPresentation,
  getActualSessionStart,
  getLatestOnTheWayArrivalRisk,
  getBookingServiceIcon,
  formatDurationFromHours,
  formatHoursAndMinutes,
  isCompletedBookingSessionStatus,
  isTodaySessionAfterCompletedStatus,
  isVoidBookingSessionStatus,
  resolveBookingSessionBillableHours,
  resolveSessionRange,
  returnSessionFilterFromDate,
} from '../../utils';
import { removeSearchParam } from '@/utils/removeSearchParams';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getBookingSession,
  sendSessionPushNotification,
} from '../../api/sessions';
// types
import { BookingSession } from '@/features/bookings-management/types';
import { LabelColors } from '@/types';
import { useGetAccess } from '@/hooks/useAccess';
import { use3DotMenu } from '@/hooks/use3DotMenu';
import useDeleteBookingSession from '@/features/bookings-management/hooks/useDeleteBookingSession';
import { BookingSessionChatActions } from './BookingSessionChatActions';

type SessionsTableRowProps = {
  row: BookingSession;
  showLiveStatus?: boolean;
  referenceTime?: number | null;
  onSelectSession?: (sessionId: number) => void;
  isSelected?: boolean;
  isExpanded?: boolean;
  highlightCompleted?: boolean;
  showCompletedDivider?: boolean;
};

// ----------------------------------------------------------------------

export default function SessionsTableRow({
  row,
  showLiveStatus = false,
  referenceTime,
  onSelectSession,
  isSelected = false,
  isExpanded = false,
  highlightCompleted = false,
  showCompletedDivider = false,
}: SessionsTableRowProps) {
  const {
    id,
    booking_id,
    user,
    address,
    booking_session_cleaners,
    booking_session_payment_status,
    booking_session_status,
    service_variant,
    date,
    time,
    is_clone,
  } = row;
  const [searchParams, setSearchParams] = useSearchParams();
  const { canUpdate } = useGetAccess('bookings');
  const { isSuperAdmin } = useAuth();
  const canDeleteSession =
    isSuperAdmin === true && isVoidBookingSessionStatus(booking_session_status);
  // =========================
  //    ACCORDION, DIALOG
  // =========================

  // DIALOGS
  const {
    toggle: isCloneSessionDialogOpen,
    onOpen: openCloneSessionDialog,
    onClose: closeCloneSessionDialog,
  } = useToggle();
  const {
    toggle: isCouponCodeDialogOpen,
    onOpen: openCouponCodeDialog,
    onClose: closeCouponCodeDialog,
  } = useToggle();
  const {
    toggle: isPushDialogOpen,
    onOpen: openPushDialog,
    onClose: closePushDialog,
  } = useToggle();
  const {
    toggle: isDeleteSessionDialogOpen,
    onOpen: openDeleteSessionDialog,
    onClose: closeDeleteSessionDialog,
  } = useToggle();

  const { openMenu, handleOpenMenu, handleCloseMenu } = use3DotMenu();
  const handleOpenMenuWithStop = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleOpenMenu(event);
  };

  const handleCloneSession = () => {
    openCloneSessionDialog();
    handleCloseMenu();
  };

  const handleApplyCoupon = () => {
    openCouponCodeDialog();
    handleCloseMenu();
  };

  const handleDeleteSession = () => {
    if (!canDeleteSession) return;
    openDeleteSessionDialog();
    handleCloseMenu();
  };

  const [pushTitle, setPushTitle] = useState('Heads up');
  const [pushBody, setPushBody] = useState('');
  const { successMsg, errorMsg } = useSnackbarMsg();
  const { deleteSessionMutation } = useDeleteBookingSession({
    onSuccessFn: (bookingSessionId) => {
      closeDeleteSessionDialog();
      if (searchParams.get('sessionID') === String(bookingSessionId)) {
        removeSearchParam(searchParams, 'sessionID', setSearchParams);
      }
    },
  });
  const sendPushMutation = useMutation({
    mutationFn: (body: string) =>
      sendSessionPushNotification({
        providerId: booking_session_cleaners?.[0]?.id ?? 0,
        title: pushTitle || 'Heads up',
        body,
        data: {
          initialPageName: 'jobDetails',
          jobID: String(id),
        },
      }),
    onSuccess: () => {
      successMsg('Push notification sent');
      setPushBody('');
      closePushDialog();
    },
    onError: (error) => {
      errorMsg(error, 'Failed to send push notification');
    },
  });

  const handleSendPush = () => {
    if (!pushBody.trim()) return;
    sendPushMutation.mutate(pushBody.trim());
  };

  const handleConfirmDeleteSession = () => {
    if (!canDeleteSession) return;
    deleteSessionMutation.mutate(id);
  };

  // ACCORDION
  const {
    toggle: isAccordionOpen,
    onToggle: toggleAccordion,
    setToggle: setIsAccordionOpen,
  } = useToggle(isExpanded);

  useEffect(() => {
    setIsAccordionOpen(isExpanded);
  }, [isExpanded, setIsAccordionOpen]);

  const { data: detailedBookingSession } = useQuery({
    queryKey: ['bookingSession', String(id)],
    queryFn: () => getBookingSession(id),
    enabled: isAccordionOpen,
    staleTime: 0,
  });

  const resolvedRow = detailedBookingSession ?? row;

  const handleToggleAccordion = () => {
    if (!isAccordionOpen) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('sessionID', String(id));
      setSearchParams(nextSearchParams);
    } else {
      removeSearchParam(searchParams, 'sessionID', setSearchParams);
    }
    toggleAccordion();
  };

  const serviceIcon = getBookingServiceIcon(service_variant);

  const renderAssignedCleaner = () => (
    <Stack direction="row" spacing={1}>
      {booking_session_cleaners.map((cleaner) => (
        <Chip
          label={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {`${cleaner?.first_name ?? 'N/A'} ${cleaner?.last_name ?? 'N/A'}`}
              {cleaner?.seen_by_provider ? (
                <Iconify
                  icon={'eva:eye-fill'}
                  width={16}
                  height={16}
                  sx={{ color: 'success.dark', marginLeft: 1 }}
                  title="Seen"
                />
              ) : (
                <Iconify
                  icon={'eva:eye-fill'}
                  width={16}
                  height={16}
                  sx={{ opacity: 0.2, marginLeft: 1 }}
                  title="Not Seen"
                />
              )}
              {cleaner?.accepted_by_provider ? (
                <Iconify
                  icon={'eva:checkmark-circle-2-fill'}
                  width={16}
                  height={16}
                  sx={{ color: 'success.dark', marginLeft: 1 }}
                  title="Accepted"
                />
              ) : (
                <Iconify
                  icon={'eva:checkmark-circle-2-fill'}
                  width={16}
                  height={16}
                  sx={{ opacity: 0.2, marginLeft: 1 }}
                  title="Not Accepted"
                />
              )}
            </span>
          }
          key={cleaner?.id}
          color="default"
          sx={{ cursor: 'pointer' }}
          component={Link}
          to={providersPath('edit', {
            userId: String(cleaner.id),
            tabSid: 'details',
          })}
          size="small"
        />
      ))}
    </Stack>
  );

  const normalizedStatusValue = booking_session_status?.value?.toUpperCase();
  const isLiveStatus =
    normalizedStatusValue && LIVE_STATUS_VALUES.includes(normalizedStatusValue);
  const isCompletedStatus = isCompletedBookingSessionStatus(
    booking_session_status
  );
  const isAfterCompletedStatus = isTodaySessionAfterCompletedStatus(
    booking_session_status
  );
  const shouldHighlightCompleted = highlightCompleted && isCompletedStatus;
  const hasReferenceTime = typeof referenceTime === 'number';
  const shouldCheckSchedule = showLiveStatus && hasReferenceTime;
  const progressEligible = shouldCheckSchedule && isLiveStatus;
  const sessionRange = useMemo(
    () => resolveSessionRange(resolvedRow),
    [resolvedRow]
  );
  const billableHours = useMemo(
    () => resolveBookingSessionBillableHours(resolvedRow),
    [resolvedRow]
  );
  const actualStartDate = useMemo(
    () => getActualSessionStart(resolvedRow),
    [resolvedRow]
  );
  const latestOnTheWayArrivalRisk = useMemo(
    () => getLatestOnTheWayArrivalRisk(resolvedRow),
    [resolvedRow]
  );
  const arrivalRiskWarning = useMemo(() => {
    if (
      !showLiveStatus ||
      !latestOnTheWayArrivalRisk ||
      !['at_risk', 'late'].includes(latestOnTheWayArrivalRisk.status)
    ) {
      return null;
    }

    return getArrivalRiskPresentation(latestOnTheWayArrivalRisk);
  }, [latestOnTheWayArrivalRisk, showLiveStatus]);

  const liveSessionProgress = useMemo(() => {
    if (!progressEligible || typeof referenceTime !== 'number' || !sessionRange)
      return null;

    const now = new Date(referenceTime);
    if (Number.isNaN(now.getTime())) return null;

    if (now < sessionRange.start || now > sessionRange.end) return null;
    const totalMs = sessionRange.end.getTime() - sessionRange.start.getTime();
    if (totalMs <= 0) return null;

    const elapsedMs = now.getTime() - sessionRange.start.getTime();
    const progress = Math.min(Math.max((elapsedMs / totalMs) * 100, 0), 100);

    const remainingMs = Math.max(sessionRange.end.getTime() - now.getTime(), 0);
    const remainingMinutes = Math.max(Math.floor(remainingMs / 60000), 0);
    return {
      progress,
      label:
        remainingMinutes > 0
          ? `${formatHoursAndMinutes(remainingMinutes)} remaining`
          : 'Ending now',
    };
  }, [progressEligible, referenceTime, sessionRange]);

  const overdueStartInfo = useMemo(() => {
    if (
      !shouldCheckSchedule ||
      !sessionRange ||
      typeof referenceTime !== 'number' ||
      isLiveStatus ||
      actualStartDate
    ) {
      return null;
    }

    const now = new Date(referenceTime);
    if (Number.isNaN(now.getTime())) return null;

    if (now < sessionRange.start || now > sessionRange.end) return null;

    const minutesLate = Math.floor(
      (now.getTime() - sessionRange.start.getTime()) / 60000
    );

    if (minutesLate <= 0) return null;

    return { minutesLate };
  }, [
    actualStartDate,
    isLiveStatus,
    referenceTime,
    sessionRange,
    shouldCheckSchedule,
  ]);

  const startDelayInfo = useMemo(() => {
    if (!showLiveStatus || !actualStartDate || !sessionRange) return null;
    const realStart: Date = actualStartDate;
    if (realStart <= sessionRange.start) return null;

    const delayMinutes = Math.ceil(
      (realStart.getTime() - sessionRange.start.getTime()) / 60000
    );
    if (delayMinutes <= 0) return null;

    return { delayMinutes };
  }, [actualStartDate, sessionRange, showLiveStatus]);

  const overdueCompletionInfo = useMemo(() => {
    if (
      !showLiveStatus ||
      !sessionRange ||
      typeof referenceTime !== 'number' ||
      isCompletedStatus ||
      isAfterCompletedStatus
    ) {
      return null;
    }

    const now = new Date(referenceTime);
    if (Number.isNaN(now.getTime()) || now <= sessionRange.end) return null;

    const minutesOver = Math.floor(
      (now.getTime() - sessionRange.end.getTime()) / 60000
    );

    if (minutesOver <= 0) return null;

    return { minutesOver };
  }, [
    isAfterCompletedStatus,
    isCompletedStatus,
    referenceTime,
    sessionRange,
    showLiveStatus,
  ]);
  return (
    <>
      <TableRow
        hover
        onClick={() => onSelectSession?.(id)}
        sx={(theme) => ({
          cursor: 'pointer',
          verticalAlign: 'center',
          backgroundColor:
            isAccordionOpen || isSelected
              ? theme.palette.primary.lighter
              : shouldHighlightCompleted
              ? alpha(theme.palette.success.main, 0.08)
              : 'inherit',
          '&:hover': {
            backgroundColor:
              isAccordionOpen || isSelected
                ? theme.palette.primary.lighter
                : shouldHighlightCompleted
                ? alpha(theme.palette.success.main, 0.12)
                : undefined,
          },
          borderLeft: `4px solid ${
            isSelected
              ? theme.palette.primary.main
              : shouldHighlightCompleted
              ? theme.palette.success.main
              : 'transparent'
          }`,
          '& > .MuiTableCell-root': {
            borderTop: showCompletedDivider
              ? `2px solid ${theme.palette.success.main}`
              : undefined,
            py: 1.25,
          },
        })}
      >
        {/* ID */}
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <TableRowLink
              linkText={id ?? 'N/A'}
              linkTo={bookingsManagementPath('view', {
                bookingId: String(booking_id),
                bookingTabSid: 'booking-sessions',
                sessionFilter: `${returnSessionFilterFromDate(
                  date
                )}?sessionID=${id}`,
              })}
              sx={{ width: 'fit-content', ml: -0.5 }}
              target="_blank"
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              {is_clone && (
                <Iconify
                  icon={'flowbite:file-clone-solid'}
                  sx={{ color: 'info.main' }}
                />
              )}
              {serviceIcon && (
                <Iconify
                  icon={serviceIcon.icon}
                  title={serviceIcon.title}
                  sx={serviceIcon.sx}
                />
              )}
              {row.linen && (
                <Iconify
                  icon="openmoji:bed-linen"
                  title="Linen"
                  sx={linenIconSx}
                />
              )}
            </Stack>
          </Stack>
        </TableCell>

        {/* SERVICE TYPE + NAME */}
        <TableCell align="left">
          <Typography noWrap fontSize={14} fontWeight={500}>
            {service_variant?.service.name ?? 'N/A'}
          </Typography>
          <Typography noWrap fontSize={14} fontWeight={500}>
            {service_variant?.name ?? 'N/A'}
          </Typography>
        </TableCell>

        {/* DATE + TIME */}
        <TableCell align="left">
          <Typography noWrap fontSize={14}>
            {format(new Date(date), 'EEE, dd MMM yyyy')}
          </Typography>
        </TableCell>
        <TableCell align="left">
          <Stack spacing={0.25}>
            <Typography noWrap fontSize={14}>
              {time}
            </Typography>
            {showLiveStatus && actualStartDate && (
              <Typography variant="caption" color="text.secondary" noWrap>
                Actual start {format(actualStartDate, 'HH:mm')}
              </Typography>
            )}
          </Stack>
        </TableCell>

        {/* TOTAL HOURS */}
        <TableCell align="left">
          {formatDurationFromHours(billableHours)}
        </TableCell>

        {/* CUSTOMER DETAILS */}
        <TableCell align="left">
          <Stack direction="column" spacing={0}>
            <TableRowLink
              linkText={user?.first_name + ' ' + user?.last_name}
              linkTo={clientManagementPath('edit', {
                userId: String(user.id),
                tabSid: 'details',
              })}
            />
            <TableRowLink
              linkText={user?.email ?? 'N/A'}
              linkTo={`mailto:${user?.email ?? ''}`}
            />
          </Stack>
        </TableCell>

        {/* CLEANER */}
        <TableCell align="left">
          {!!booking_session_cleaners.length && renderAssignedCleaner()}
        </TableCell>

        {/* ADDRESS */}
        <TableCell align="left">
          <AddressWithGoogleMapsIconButton
            formatted_address={address.formatted_address}
            postcode={address.postcode}
            maxLines={2}
          />
        </TableCell>

        {/* STATUSUSES */}
        <TableCell align="left">
          <Stack spacing={0.75}>
            <Label
              color={
                (booking_session_payment_status.style_class as LabelColors) ||
                'info'
              }
            >
              {booking_session_payment_status.display || 'N/A'}
            </Label>
            <Label
              color={
                (booking_session_status.style_class as LabelColors) || 'info'
              }
            >
              {booking_session_status.display || 'N/A'}
            </Label>
            {arrivalRiskWarning && (
              <Label
                color={arrivalRiskWarning.color}
                variant={arrivalRiskWarning.variant}
                sx={{ width: 'fit-content' }}
              >
                {arrivalRiskWarning.text}
              </Label>
            )}
            {overdueStartInfo && (
              <Label
                color="warning"
                variant="ghost"
                sx={{ width: 'fit-content' }}
              >
                Start overdue ·{' '}
                {`${formatHoursAndMinutes(overdueStartInfo.minutesLate)} late`}
              </Label>
            )}
            {startDelayInfo && (
              <Label
                color="warning"
                variant="outlined"
                sx={{ width: 'fit-content' }}
              >
                Started late ·{' '}
                {`${formatHoursAndMinutes(startDelayInfo.delayMinutes)} delay`}
              </Label>
            )}
            {overdueCompletionInfo && (
              <Label
                color="error"
                variant="ghost"
                sx={{ width: 'fit-content' }}
              >
                Time exceeded ·{' '}
                {`${formatHoursAndMinutes(
                  overdueCompletionInfo.minutesOver
                )} over`}
              </Label>
            )}
            {liveSessionProgress && (
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={liveIndicatorSx} />
                  <Typography
                    variant="caption"
                    color="success.dark"
                    sx={{ fontWeight: 500 }}
                  >
                    {booking_session_status.display} ·{' '}
                    {liveSessionProgress.label}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={liveSessionProgress.progress}
                  color="success"
                  sx={{ height: 6, borderRadius: 999 }}
                />
              </Stack>
            )}
          </Stack>
        </TableCell>

        <TableCell align="right" sx={{ px: 0 }}>
          {/* originally this menu is kept in the accordion summary but that part of the component is hidden here */}
          {/* there's a bit of duplication going on here */}
          <Stack direction="row" spacing={1} alignItems="center">
            <BookingSessionChatActions bookingSession={row} compact />
            {/* 3-DOT-MENU */}
            <TableMoreMenu
              open={openMenu}
              onOpen={handleOpenMenuWithStop}
              onClose={handleCloseMenu}
              iconHeight={15}
              iconWidth={15}
              popOverWidth={250}
              arrow="top-right"
              actions={
                <>
                  {canUpdate && showLiveStatus && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        openPushDialog();
                        handleCloseMenu();
                      }}
                    >
                      <Iconify
                        icon={'tabler:device-mobile-message'}
                        width={20}
                        height={20}
                      />
                      Send push notification
                    </MenuItem>
                  )}
                  {canUpdate && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloneSession();
                      }}
                    >
                      <Iconify
                        icon={'clarity:clone-line'}
                        width={18}
                        height={18}
                      />
                      Clone session
                    </MenuItem>
                  )}
                  {canUpdate && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyCoupon();
                      }}
                    >
                      <Iconify
                        icon={'streamline:discount-percent-coupon'}
                        width={18}
                        height={18}
                      />
                      Apply Coupon
                    </MenuItem>
                  )}
                  {canDeleteSession && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession();
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <Iconify
                        icon={'eva:trash-2-outline'}
                        width={18}
                        height={18}
                      />
                      Delete
                    </MenuItem>
                  )}
                </>
              }
            />
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleToggleAccordion();
              }}
              sx={{
                transform: isAccordionOpen ? `rotate(180deg)` : '',
                transition: `transform 0.3s`,
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <Fade
        in={isAccordionOpen}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 500, exit: 300 }}
      >
        <TableRow>
          <TableCell colSpan={100}>
            <VBMAccordion bookingSession={row} hideSummary />
          </TableCell>
        </TableRow>
      </Fade>
      <Dialog open={isPushDialogOpen} onClose={closePushDialog} fullWidth>
        <DialogTitle>Send push notification</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Deliver a custom push message to the assigned provider.
          </DialogContentText>
          <TextField
            fullWidth
            label="Title"
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Message"
            value={pushBody}
            onChange={(e) => setPushBody(e.target.value)}
            placeholder="Enter the message to send"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closePushDialog}
            disabled={sendPushMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendPush}
            variant="contained"
            disabled={!pushBody.trim() || sendPushMutation.isLoading}
          >
            {sendPushMutation.isLoading ? 'Sending…' : 'Send push'}
          </Button>
        </DialogActions>
      </Dialog>
      {isCouponCodeDialogOpen && (
        <VBMApplyCouponDialog
          isDialogOpen={isCouponCodeDialogOpen}
          closeDialog={closeCouponCodeDialog}
          serviceVariantId={service_variant.id}
          bookingSessionId={id}
        />
      )}
      {isCloneSessionDialogOpen && (
        <VBMCloneSessionDialog
          isDialogOpen={isCloneSessionDialogOpen}
          closeDialog={closeCloneSessionDialog}
          currentBookingSession={row}
        />
      )}
      <DialogSimple
        dialogTitle={`Delete session #${id}?`}
        open={isDeleteSessionDialogOpen}
        onClickButtonClose={closeDeleteSessionDialog}
        onClickButtonLoading={handleConfirmDeleteSession}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
        isSubmitting={deleteSessionMutation.isLoading}
      >
        <Typography sx={{ px: 3, pb: 2 }}>
          This will delete this booking session. This action cannot be undone.
        </Typography>
      </DialogSimple>
    </>
  );
}

const liveIndicatorSx = {
  position: 'relative',
  width: 10,
  height: 10,
  borderRadius: '50%',
  bgcolor: 'success.main',
  flexShrink: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    bgcolor: 'success.main',
    opacity: 0.4,
    animation: 'pulse 1.5s ease-out infinite',
  },
  '@keyframes pulse': {
    '0%': { transform: 'scale(1)', opacity: 0.7 },
    '70%': { transform: 'scale(2)', opacity: 0 },
    '100%': { transform: 'scale(1)', opacity: 0 },
  },
} as const;

const LIVE_STATUS_VALUES = ['ON_THE_WAY', 'IN_PROGRESS'];

const linenIconSx = {
  color: 'info.main',
  '& [fill]:not([fill="none"])': {
    fill: 'currentColor !important',
  },
  '& [stroke]:not([stroke="none"])': {
    stroke: 'currentColor !important',
  },
} as const;
